import { useRef } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import {
    useOverviewBundle,
    getOverviewBundle,
    getOverviewBundleExcludeEvsSections,
    getOverviewBundleExcludeEvsQueryKey,
    useDomainStats,
    getDomainStats,
} from '../../../hooks/reporting';
import {
    getOverviewBundleLocationId,
    REPORTING_OVERVIEW_BUNDLE_LIMIT,
} from '../../../hooks/reporting/reportingBundleDomainStats';
import {
    getOverviewBundleQueryKey,
    useOverviewBundleBase,
    useOverviewBundleOverlay,
} from '../../../hooks/reporting/OverviewReportingBundleContext';
import {
    getBundleSectionLoadState,
    getWidgetBundleSection,
    getWidgetDataNeeds,
    latchSettledDisplayParams,
    mergeBaseSections,
    mergeExcludeEvsOverlay,
    missingBundleSections,
    resolveBundleDisplayParams,
    resolveBundleSection,
    timeseriesLacksBySource,
} from '../../../hooks/reporting/overviewBundleSections';
import type {
    ReportingComparison,
    DomainStatItem,
    DomainType,
    OverviewBundlePartialResponse,
    OverviewBundleResponse,
    OverviewBundleSection,
    OverviewRankingsResponse,
    OverviewSummaryResponse,
    OverviewTimeseriesResponse,
} from '../../../types/reporting';
import type { WidgetConfig } from './types';
import { supportsMarketplaceBreakdown } from './types';

const DOMAIN_WIDGET_LIMIT = REPORTING_OVERVIEW_BUNDLE_LIMIT;

export type { WidgetDataNeeds } from '../../../hooks/reporting/overviewBundleSections';
export { getWidgetDataNeeds } from '../../../hooks/reporting/overviewBundleSections';

export interface WidgetSharedParams {
    startDate: string;
    endDate: string;
    locationId?: string;
    isGm?: boolean;
    granularity?: 'day' | 'week' | 'biweekly' | 'month';
    preset?: string;
    comparison?: ReportingComparison;
}

function getDomainStatsLocationId(
    domain: DomainType,
    params: WidgetSharedParams,
): string | undefined {
    if (domain === 'location') {
        return params.isGm ? params.locationId : undefined;
    }
    return params.locationId;
}

function buildDomainEndpointOptions(
    domain: DomainType,
    params: WidgetSharedParams,
    excludeEvs: boolean,
    endpoint: NonNullable<ReturnType<typeof getWidgetDataNeeds>['domainEndpoint']>,
) {
    return {
        locationId: getDomainStatsLocationId(domain, params),
        sortBy: 'orderVolume' as const,
        sortOrder: 'desc' as const,
        // Capped unless this widget asked for all rows.
        limit: endpoint.allRows ? undefined : DOMAIN_WIDGET_LIMIT,
        includeBySource: endpoint.includeBySource,
        preset: params.preset,
        comparison: params.comparison,
        excludeEvs,
    };
}

function buildBundleInput(
    params: WidgetSharedParams,
    limit: number,
    locationId?: string,
    options?: {
        excludeEvs?: boolean;
        excludeEvsSectionsOnly?: boolean;
        excludeEvsSections?: OverviewBundleSection[];
    },
) {
    const { startDate, endDate, granularity = 'week', preset, comparison } = params;
    return {
        startDate,
        endDate,
        granularity,
        locationId: locationId ?? params.locationId,
        limit,
        preset,
        comparison,
        includeComparison: !!comparison,
        ...options,
    };
}

function buildExportData(
    config: WidgetConfig,
    needs: ReturnType<typeof getWidgetDataNeeds>,
    summaryData?: OverviewSummaryResponse,
    timeseriesData?: OverviewTimeseriesResponse,
    rankingsData?: OverviewRankingsResponse,
    domainStatsItems?: DomainStatItem[],
): any[] | undefined {
    if (needs.summary && summaryData) {
        return [summaryData];
    }
    if (needs.timeseries && timeseriesData) {
        return timeseriesData.data;
    }
    if (needs.domainEndpoint && domainStatsItems) {
        return domainStatsItems;
    }
    if (needs.rankings && rankingsData) {
        return (rankingsData as unknown as Record<string, unknown>)[config.dataKey || 'locations'] as unknown[] | undefined;
    }
    return undefined;
}

function getWidgetBundleSectionForConfig(config: WidgetConfig): OverviewBundleSection | null {
    return getWidgetBundleSection(config, getWidgetDataNeeds(config));
}

export function useWidgetData(config: WidgetConfig, params: WidgetSharedParams) {
    const widgetExcludeEvs = config.excludeEvs ?? false;
    const needs = getWidgetDataNeeds(config);
    const bundleSection = getWidgetBundleSectionForConfig(config);
    const {
        startDate,
        endDate,
        granularity = 'week',
        preset,
        comparison,
    } = params;

    // Base for all widgets. Non-EV widgets normally skip the overlay store so its fetch
    // ticks do not re-render them — except while the `all` slice has no data for this
    // section (Exclude EVs just turned off), when the EV numbers already on screen are
    // the only thing to show.
    const sharedBase = useOverviewBundleBase();
    const sharedBaseHasSection = Boolean(
        bundleSection && sharedBase?.baseQuery.data?.[bundleSection] !== undefined,
    );
    const sharedOverlay = useOverviewBundleOverlay(widgetExcludeEvs || !sharedBaseHasSection);
    const hasSharedBundle = !!sharedBase;
    const bundleLocationId = getOverviewBundleLocationId(params.locationId);
    // Marketplace / all-rows use /domain/stats — not the overview bundle.
    const needsOverviewBundle = needs.summary || needs.timeseries || needs.rankings;

    const localBundleQuery = useOverviewBundle(
        startDate,
        endDate,
        granularity,
        bundleLocationId,
        DOMAIN_WIDGET_LIMIT,
        preset,
        comparison,
        widgetExcludeEvs,
        {
            enabled: needsOverviewBundle && !hasSharedBundle,
            sections: (!widgetExcludeEvs && bundleSection) ? [bundleSection] : undefined,
        },
    );

    const baseBundle = sharedBase?.baseQuery.data ?? localBundleQuery.data;
    const baseQuery = sharedBase?.baseQuery ?? localBundleQuery;
    const overlayQuery = sharedOverlay?.excludeEvsOverlayQuery;
    const needsOverlaySection = Boolean(
        widgetExcludeEvs
        && bundleSection
        && sharedOverlay?.excludeEvsSections.includes(bundleSection),
    );

    // Only current-range overlay data counts as loaded.
    const excludeEvsOverlay = (
        overlayQuery?.data
        && overlayQuery.isSuccess
        && !overlayQuery.isPlaceholderData
    )
        ? overlayQuery.data
        : undefined;
    /** Last range's EV slice — display-only, always rendered stale (fade + spinner). */
    const staleExcludeEvsOverlay = overlayQuery?.isPlaceholderData
        ? overlayQuery.data
        : undefined;

    const baseIsStale = !!baseQuery.isPlaceholderData;
    const overlayHasSection = Boolean(
        bundleSection && excludeEvsOverlay?.[bundleSection] !== undefined,
    );
    const baseHasSection = Boolean(
        bundleSection && baseBundle?.[bundleSection] !== undefined,
    );

    /**
     * Fallback order for an EV widget with no current-range EV data: prefer the `all`
     * slice while it is fresh (Exclude EVs toggled mid-range — keep what is on screen),
     * otherwise last range's EV numbers beat last range's `all` numbers.
     */
    const preferStaleOverlay = Boolean(
        widgetExcludeEvs
        && !overlayHasSection
        && staleExcludeEvsOverlay
        && (baseIsStale || !baseHasSection),
    );
    /**
     * Exclude EVs just turned off — fade the EV numbers this widget was showing until the
     * `all` refill lands. Latched on the toggle so a cold load (where an EV sibling may
     * fill the overlay first) never paints another slice's numbers.
     */
    const evFallbackRef = useRef(false);
    const prevWidgetExcludeEvsRef = useRef(widgetExcludeEvs);
    if (prevWidgetExcludeEvsRef.current !== widgetExcludeEvs) {
        evFallbackRef.current = !widgetExcludeEvs && overlayHasSection;
        prevWidgetExcludeEvsRef.current = widgetExcludeEvs;
    }
    if (widgetExcludeEvs || baseHasSection) {
        evFallbackRef.current = false;
    }
    const preferEvFallback = evFallbackRef.current && overlayHasSection;
    const displayOverlay = excludeEvsOverlay
        ?? (preferStaleOverlay ? staleExcludeEvsOverlay : undefined);
    const readFromOverlay = widgetExcludeEvs || preferEvFallback;
    const staleOverlayHasSection = Boolean(
        preferStaleOverlay
        && bundleSection
        && staleExcludeEvsOverlay?.[bundleSection] !== undefined,
    );
    /** Provider refills a newly needed `all` section from an effect — one tick behind. */
    const baseSectionPending = Boolean(
        !widgetExcludeEvs
        && bundleSection
        && !baseHasSection
        && sharedBase?.allSections.includes(bundleSection),
    );

    // Params paired with on-screen rows. Provider latches these with placeholder data;
    // local queries (no shared bundle) latch the same way so we never format last range's
    // day buckets with the new biweekly/week window (e.g. "Aug 3 - Aug 9").
    const localSettledParamsRef = useRef(params);
    const localDisplayParams = latchSettledDisplayParams(
        localSettledParamsRef,
        params,
        !hasSharedBundle && !!localBundleQuery.isPlaceholderData,
    );
    const dataParams = resolveBundleDisplayParams({
        liveParams: params,
        baseIsStale,
        baseDisplayParams: sharedBase?.displayParams ?? localDisplayParams,
        preferStaleOverlay,
        overlayDisplayParams: sharedOverlay?.displayParams ?? localDisplayParams,
    });

    const domainEndpoint = needs.domainEndpoint;
    const domainStatsDomain = domainEndpoint?.domain ?? needs.domain ?? 'location';
    const domainStatsQuery = useDomainStats(domainStatsDomain, startDate, endDate, {
        ...(domainEndpoint
            ? buildDomainEndpointOptions(domainStatsDomain, params, widgetExcludeEvs, domainEndpoint)
            : {
                locationId: getDomainStatsLocationId(domainStatsDomain, params),
                sortBy: 'orderVolume' as const,
                sortOrder: 'desc' as const,
                limit: DOMAIN_WIDGET_LIMIT,
                includeBySource: false,
                preset: params.preset,
                comparison: params.comparison,
                excludeEvs: widgetExcludeEvs,
            }),
        enabled: !!domainEndpoint,
    });

    const summaryData = needs.summary
        ? resolveBundleSection(baseBundle, displayOverlay, 'summary', readFromOverlay) as
            OverviewSummaryResponse | undefined
        : undefined;
    const timeseriesData = needs.timeseries
        ? resolveBundleSection(baseBundle, displayOverlay, 'timeseries', readFromOverlay) as
            OverviewTimeseriesResponse | undefined
        : undefined;
    const rankingsData = needs.rankings
        ? resolveBundleSection(baseBundle, displayOverlay, 'rankings', readFromOverlay) as
            OverviewRankingsResponse | undefined
        : undefined;
    const domainStatsItems = domainEndpoint ? domainStatsQuery.data?.items : undefined;

    // Only treat THIS widget's section as loading when it is missing.
    // Sibling section fetches (e.g. rankings while timeseries is cached) must not fade it.
    const sectionFetching = bundleSection
        ? (
            (
                !widgetExcludeEvs
                && !baseHasSection
                && (baseQuery.isFetching || !!baseQuery.isPlaceholderData || baseSectionPending)
            )
            || (
                !widgetExcludeEvs
                && baseHasSection
                && !!baseQuery.isPlaceholderData
            )
            || (
                widgetExcludeEvs
                && needsOverlaySection
                && !overlayHasSection
            )
        )
        : false;
    const { isInitialLoading: sectionInitialLoading, isRefetching: sectionRefetching } =
        getBundleSectionLoadState({
            sectionFetching,
            // Overlay only counts when this widget will actually render it.
            hasBaseData: baseHasSection
                || (readFromOverlay && overlayHasSection)
                || staleOverlayHasSection,
            widgetExcludeEvs,
            needsOverlay: needsOverlaySection,
            overlayHasSection,
        });

    const domainStatsHasData = !!domainStatsQuery.data;
    const domainStatsInitialLoading = !!domainEndpoint
        && domainStatsQuery.isFetching
        && !domainStatsHasData;
    const domainStatsRefetching = !!domainEndpoint
        && domainStatsQuery.isFetching
        && domainStatsHasData;

    const rankingsLoading = domainEndpoint
        ? domainStatsInitialLoading
        : needs.rankings
          ? sectionInitialLoading
          : false;

    const isRefetching = needs.summary
        ? sectionRefetching
        : needs.timeseries
          ? sectionRefetching
          : domainEndpoint
            ? domainStatsRefetching
            : needs.rankings
              ? sectionRefetching
              : false;

    return {
        summaryData,
        summaryLoading: needs.summary ? sectionInitialLoading : false,
        summaryError: needs.summary ? baseQuery.isError : false,
        timeseriesData,
        timeseriesLoading: needs.timeseries ? sectionInitialLoading : false,
        rankingsData,
        rankingsLoading,
        domainStatsItems,
        domainStatsLoading: domainEndpoint ? domainStatsInitialLoading : false,
        isRefetching,
        comparison: dataParams.comparison,
        granularity: dataParams.granularity ?? 'week',
        preset: dataParams.preset,
        /** Window the rendered rows cover — trails with placeholder data while refetching. */
        dataStartDate: dataParams.startDate,
        dataEndDate: dataParams.endDate,
        exportData: buildExportData(
            config,
            needs,
            summaryData,
            timeseriesData,
            rankingsData,
            domainStatsItems,
        ),
    };
}

export async function getWidgetExportData(
    queryClient: QueryClient,
    sessionId: string,
    config: WidgetConfig,
    params: WidgetSharedParams,
): Promise<any[] | undefined> {
    if (!sessionId || !params.startDate || !params.endDate) {
        return undefined;
    }

    const widgetExcludeEvs = config.excludeEvs ?? false;
    const needs = getWidgetDataNeeds(config);
    const bundleSection = getWidgetBundleSectionForConfig(config);
    const {
        startDate,
        endDate,
        granularity = 'week',
        preset,
        comparison,
    } = params;

    if (needs.domainEndpoint) {
        const endpoint = needs.domainEndpoint;
        const domainOptions = buildDomainEndpointOptions(
            endpoint.domain,
            params,
            widgetExcludeEvs,
            endpoint,
        );
        const { items } = await getDomainStats(sessionId, {
            domain: endpoint.domain,
            startDate,
            endDate,
            ...domainOptions,
            includeComparison: !!params.comparison,
        });
        return items;
    }

    const needsOverviewBundle = needs.summary || needs.timeseries || needs.rankings;
    const bundleLocationId = getOverviewBundleLocationId(params.locationId);

    if (needsOverviewBundle) {
        const baseKey = getOverviewBundleQueryKey(
            startDate,
            endDate,
            granularity,
            bundleLocationId,
            DOMAIN_WIDGET_LIMIT,
            preset,
            comparison,
        );
        const baseInput = buildBundleInput(params, DOMAIN_WIDGET_LIMIT, bundleLocationId);
        const baseCached = queryClient.getQueryData<OverviewBundleResponse>(baseKey);

        let overlay: OverviewBundlePartialResponse | undefined;

        const overlayKey = getOverviewBundleExcludeEvsQueryKey(
            startDate,
            endDate,
            granularity,
            bundleLocationId,
            DOMAIN_WIDGET_LIMIT,
            preset,
            comparison,
        );

        // Selective `all` (or skip when this widget is EV-only), then excludeEvsSectionsOnly.
        // Combined dual-cube responses can exceed Lambda's 6MB limit.
        const timeseriesIncludeBySource = needs.timeseries
            && Boolean(config.showMarketplaceBreakdown)
            && supportsMarketplaceBreakdown(config.domain, config.widgetType, config.metric);

        let base = baseCached;
        if (!widgetExcludeEvs && bundleSection) {
            const missingBase = missingBundleSections(baseCached, [bundleSection]);
            const needsBySourceUpgrade = timeseriesIncludeBySource
                && bundleSection === 'timeseries'
                && timeseriesLacksBySource(baseCached?.timeseries);
            if (missingBase.length === 0 && baseCached && !needsBySourceUpgrade) {
                base = baseCached;
            } else {
                base = await queryClient.fetchQuery({
                    queryKey: baseKey,
                    queryFn: async () => {
                        const prev = queryClient.getQueryData<OverviewBundleResponse>(baseKey);
                        const requested = Array.from(new Set([
                            ...(prev?.sections ?? []),
                            bundleSection,
                        ]));
                        const missing = missingBundleSections(prev, [bundleSection]);
                        const upgrade = timeseriesIncludeBySource
                            && bundleSection === 'timeseries'
                            && timeseriesLacksBySource(prev?.timeseries);
                        const sectionsToFetch = upgrade
                            ? Array.from(new Set([...missing, 'timeseries' as const]))
                            : missing;
                        if (sectionsToFetch.length === 0 && prev) {
                            return {
                                ...prev,
                                sectionsOnly: true as const,
                                sections: requested,
                            };
                        }
                        const delta = await getOverviewBundle(sessionId, {
                            ...baseInput,
                            sections: sectionsToFetch.length > 0 ? sectionsToFetch : [bundleSection],
                            timeseriesIncludeBySource,
                        });
                        return mergeBaseSections(prev, delta, requested);
                    },
                    staleTime: 1000 * 60 * 5,
                });
            }
        }

        if (widgetExcludeEvs && bundleSection) {
            const prevOverlay = queryClient.getQueryData<OverviewBundlePartialResponse>(overlayKey);
            const missing = missingBundleSections(prevOverlay, [bundleSection]);
            const needsBySourceUpgrade = timeseriesIncludeBySource
                && bundleSection === 'timeseries'
                && timeseriesLacksBySource(prevOverlay?.timeseries);
            if (missing.length === 0 && prevOverlay && !needsBySourceUpgrade) {
                overlay = prevOverlay;
            } else {
                const delta = await getOverviewBundleExcludeEvsSections(sessionId, {
                    ...baseInput,
                    excludeEvsSectionsOnly: true,
                    excludeEvsSections: (needsBySourceUpgrade
                        ? Array.from(new Set([...missing, 'timeseries' as const]))
                        : missing.length > 0 ? missing : [bundleSection]),
                    timeseriesIncludeBySource,
                });
                overlay = mergeExcludeEvsOverlay(
                    prevOverlay,
                    delta,
                    Array.from(new Set([
                        ...(prevOverlay?.excludeEvsSections ?? []),
                        bundleSection,
                    ])),
                );
                queryClient.setQueryData(overlayKey, overlay);
            }
        }

        if (needs.summary) {
            const summary = resolveBundleSection(base, overlay, 'summary', widgetExcludeEvs) as
                OverviewSummaryResponse | undefined;
            return summary ? [summary] : undefined;
        }
        if (needs.timeseries) {
            const timeseries = resolveBundleSection(base, overlay, 'timeseries', widgetExcludeEvs) as
                OverviewTimeseriesResponse | undefined;
            return timeseries?.data;
        }
        if (needs.rankings) {
            const rankings = resolveBundleSection(base, overlay, 'rankings', widgetExcludeEvs) as
                OverviewRankingsResponse | undefined;
            return rankings
                ? (rankings as unknown as Record<string, unknown>)[config.dataKey || 'locations'] as unknown[] | undefined
                : undefined;
        }
    }

    return undefined;
}
